import { useEffect, useRef, useState } from 'react';
import { confirmIntake, getTicket, listIntake, requestUploadUrl, submitIntake, uploadToR2 } from '../api';
import type { Ticket } from '../types';
import { relativeTime, shortenSummary } from '../util';
import { TicketDetailSheet } from '../components/TicketDetailSheet';

type RecorderState = 'idle' | 'recording' | 'uploading' | 'classifying' | 'ready' | 'error';

export function IntakeTab() {
  const [recState, setRecState] = useState<RecorderState>('idle');
  const [recError, setRecError] = useState<string | null>(null);
  const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
  const [history, setHistory] = useState<Ticket[]>([]);
  const [textInput, setTextInput] = useState('');
  const [edits, setEdits] = useState<{ summary: string; body_md: string; priority: string }>({
    summary: '',
    body_md: '',
    priority: 'normal',
  });
  const [detailTicket, setDetailTicket] = useState<Ticket | null>(null);
  const [pulling, setPulling] = useState(false);
  const [pullDelta, setPullDelta] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startMsRef = useRef<number>(0);
  const [recSeconds, setRecSeconds] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  // Refresh history on mount and every 10s
  useEffect(() => {
    refreshHistory();
    const id = window.setInterval(refreshHistory, 10000);
    return () => clearInterval(id);
  }, []);

  // Poll active ticket while classifying
  useEffect(() => {
    if (!activeTicket || activeTicket.status !== 'pending_classify') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const updated = await getTicket(activeTicket.id);
        if (cancelled) return;
        setActiveTicket(updated);
        if (updated.classification && updated.status !== 'pending_classify') {
          setRecState('ready');
          setEdits({
            summary: updated.classification.summary,
            body_md: updated.classification.body_md,
            priority: updated.classification.priority,
          });
        }
      } catch (e) {
        console.warn('poll failed', e);
      }
    };
    const id = window.setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeTicket]);

  // Live timer while recording
  useEffect(() => {
    if (recState !== 'recording') return;
    const id = window.setInterval(() => {
      setRecSeconds(Math.floor((Date.now() - startMsRef.current) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [recState]);

  async function refreshHistory() {
    try {
      const { tickets } = await listIntake();
      setHistory(tickets.slice(0, 20));
    } catch {
      // ignore
    }
  }

  async function startRecording() {
    setRecError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => handleRecordingStop(mime);
      rec.start(250);
      recorderRef.current = rec;
      startMsRef.current = Date.now();
      setRecSeconds(0);
      setRecState('recording');
    } catch (e) {
      setRecError(`Microphone failed: ${(e as Error).message}`);
      setRecState('error');
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    streamRef.current = null;
  }

  async function handleRecordingStop(mime: string) {
    setRecState('uploading');
    try {
      const blob = new Blob(chunksRef.current, { type: mime });
      const ext = mime.includes('mp4') ? 'm4a' : 'webm';
      const { upload_url, r2_key } = await requestUploadUrl('audio', ext);
      await uploadToR2(upload_url, blob, mime);
      const { ticket_id } = await submitIntake({ source: 'voice', audio_r2_key: r2_key });
      setRecState('classifying');
      const t = await getTicket(ticket_id);
      setActiveTicket(t);
    } catch (e) {
      setRecError((e as Error).message);
      setRecState('error');
    }
  }

  async function handleImage(file: File) {
    setRecError(null);
    setRecState('uploading');
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || (file.type.includes('png') ? 'png' : 'jpg');
      const { upload_url, r2_key } = await requestUploadUrl('image', ext);
      await uploadToR2(upload_url, file, file.type || 'image/jpeg');
      const { ticket_id } = await submitIntake({ source: 'screenshot', image_r2_key: r2_key });
      setRecState('classifying');
      const t = await getTicket(ticket_id);
      setActiveTicket(t);
    } catch (e) {
      setRecError((e as Error).message);
      setRecState('error');
    }
  }

  async function submitText() {
    if (!textInput.trim()) return;
    setRecError(null);
    setRecState('classifying');
    try {
      const { ticket_id } = await submitIntake({ source: 'text', text: textInput.trim() });
      const t = await getTicket(ticket_id);
      setActiveTicket(t);
      setTextInput('');
    } catch (e) {
      setRecError((e as Error).message);
      setRecState('error');
    }
  }

  async function confirm() {
    if (!activeTicket) return;
    try {
      await confirmIntake(activeTicket.id, {
        summary: edits.summary,
        body_md: edits.body_md,
        priority: edits.priority,
      });
      setActiveTicket(null);
      setRecState('idle');
      refreshHistory();
    } catch (e) {
      setRecError((e as Error).message);
    }
  }

  function reset() {
    setActiveTicket(null);
    setRecState('idle');
    setRecError(null);
  }

  // Paste/drop image
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (recState !== 'idle') return;
      const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
      if (!item) return;
      const file = item.getAsFile();
      if (file) handleImage(file);
    };
    const onDrop = (e: DragEvent) => {
      if (recState !== 'idle') return;
      e.preventDefault();
      const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/'));
      if (file) handleImage(file);
    };
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    window.addEventListener('paste', onPaste);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragover', onDragOver);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragover', onDragOver);
    };
  }, [recState]);

  return (
    <div className="tab intake">
      <h1 className="tab-title">Intake</h1>

      {recState === 'idle' && (
        <>
          <div className="intake-grid">
            <button className="big-btn big-btn-mic" onClick={startRecording} aria-label="Record voice memo">
              <span className="big-btn-icon" aria-hidden>
                <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="3" width="6" height="12" rx="3" />
                  <path d="M5 11a7 7 0 0 0 14 0" />
                  <path d="M12 18v3" />
                </svg>
              </span>
              <span className="big-btn-label">Voice</span>
            </button>

            <label className="big-btn big-btn-camera" aria-label="Capture screenshot or photo">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImage(f);
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
              />
              <span className="big-btn-icon" aria-hidden>
                <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8h4l2-3h6l2 3h4v11H3z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </span>
              <span className="big-btn-label">Camera</span>
            </label>
          </div>

          <div className="intake-text">
            <textarea
              placeholder="Or type a quick note (paste a screenshot too)…"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows={3}
            />
            <button className="btn btn-secondary" onClick={submitText} disabled={!textInput.trim()}>
              Submit text
            </button>
          </div>
        </>
      )}

      {recState === 'recording' && (
        <div className="rec-active">
          <div className="rec-pulse" aria-hidden />
          <div className="rec-time">
            {String(Math.floor(recSeconds / 60)).padStart(2, '0')}:
            {String(recSeconds % 60).padStart(2, '0')}
          </div>
          <button className="btn btn-primary" onClick={stopRecording}>
            Stop & upload
          </button>
        </div>
      )}

      {recState === 'uploading' && <StatusCard label="Uploading…" />}
      {recState === 'classifying' && <StatusCard label="Classifying…" />}

      {recState === 'ready' && activeTicket?.classification && (
        <div className="card">
          <div className="ticket-pills">
            <span className={`pill pill-${activeTicket.classification.type}`}>
              {activeTicket.classification.type}
            </span>
            <span className={`pill pill-${activeTicket.classification.priority}`}>
              {activeTicket.classification.priority}
            </span>
            <span className="pill pill-meta">
              {Math.round(activeTicket.classification.confidence * 100)}%
            </span>
          </div>
          <label className="field">
            <span>Summary</span>
            <input
              value={edits.summary}
              onChange={(e) => setEdits({ ...edits, summary: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Body</span>
            <textarea
              rows={8}
              value={edits.body_md}
              onChange={(e) => setEdits({ ...edits, body_md: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Priority</span>
            <select
              value={edits.priority}
              onChange={(e) => setEdits({ ...edits, priority: e.target.value })}
            >
              <option value="low">low</option>
              <option value="normal">normal</option>
              <option value="high">high</option>
              <option value="urgent">urgent</option>
            </select>
          </label>
          <div className="row-actions">
            <button className="btn btn-ghost" onClick={reset}>
              Discard
            </button>
            <button className="btn btn-primary" onClick={confirm}>
              Drop in queue
            </button>
          </div>
        </div>
      )}

      {recState === 'error' && (
        <div className="card error-card">
          <div>{recError || 'Something went wrong.'}</div>
          <button className="btn btn-secondary" onClick={reset}>
            Try again
          </button>
        </div>
      )}

      <section className="history">
        <h2 className="section-title">Recent</h2>
        {history.length === 0 && <div className="muted">No tickets yet.</div>}
        <div
          className={`pull-indicator ${pulling ? 'is-pulling' : ''}`}
          style={{ height: Math.min(pullDelta, 60) }}
          aria-hidden
        >
          {pulling ? 'Refreshing…' : pullDelta > 40 ? 'Release to refresh' : pullDelta > 0 ? 'Pull to refresh' : ''}
        </div>
        <ul
          className="history-list"
          ref={listRef}
          onTouchStart={(e) => {
            if (window.scrollY > 4) return;
            touchStartYRef.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (touchStartYRef.current == null) return;
            const dy = e.touches[0].clientY - touchStartYRef.current;
            if (dy > 0 && window.scrollY <= 0) {
              setPullDelta(Math.min(dy, 80));
            }
          }}
          onTouchEnd={() => {
            const dy = pullDelta;
            touchStartYRef.current = null;
            if (dy > 40) {
              setPulling(true);
              refreshHistory().finally(() => {
                setPulling(false);
                setPullDelta(0);
              });
            } else {
              setPullDelta(0);
            }
          }}
        >
          {history.map((t) => (
            <li key={t.id} className="history-item">
              <button
                type="button"
                className="history-btn"
                onClick={() => setDetailTicket(t)}
                aria-label={`Open ticket ${shortenSummary(t.classification?.summary || t.raw_input.text || t.id, 50)}`}
              >
                <div className="history-row">
                  <span className={`pill pill-${t.classification?.type || 'meta'}`}>
                    {t.classification?.type || t.status.replace('_', ' ')}
                  </span>
                  <span className="history-time">{relativeTime(t.created_at)}</span>
                </div>
                <div className="history-summary">
                  {shortenSummary(t.classification?.summary || t.raw_input.text || '(no summary yet)')}
                </div>
                <span className="history-chevron" aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {detailTicket && (
        <TicketDetailSheet ticket={detailTicket} onClose={() => setDetailTicket(null)} />
      )}
    </div>
  );
}

function StatusCard({ label }: { label: string }) {
  return (
    <div className="card status-card">
      <div className="spinner" aria-hidden />
      <div>{label}</div>
    </div>
  );
}
